import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, isDevMode, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Angulartics2Module } from 'angulartics2';
import { BehaviorTrackerModule } from 'app/behavior-tracking/behavior-tracker.module';
import { ApiEndpointService } from 'app/core/services/api-endpoint.service';
import { ApiHealthService } from 'app/core/services/api-health.service';
import { FallbackTranslationHandler } from 'app/core/services/fallback-translation-handler';
import { JsonCacheBustInterceptor } from 'app/core/services/json-cache-bust-interceptor';
import { MockHttpInterceptor } from 'app/mocks/http-mock.interceptor';
import { SESSION_IDLE_CONFIG } from 'app/session-idle/config/session-idle.config';
import { SessionIdleModule } from 'app/session-idle/session-idle.module';
import { DebugButtonComponent } from 'app/shared/components/debug-tools/debug-button/debug-button.component';
import { environment } from 'environments/environment';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaModule, ReCaptchaV3Service } from 'ng-recaptcha';
import { CookieService } from 'ngx-cookie-service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { defaultIfEmpty, firstValueFrom, of } from 'rxjs';
import { AccountModule } from './account/account.module';
import { AddressBookModule } from './address-book/address-book.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BillingModule } from './billing/billing.module';
import { CoreModule } from './core/core.module';
import { EnvironmentService } from './core/services/environment.service';
import { HtmlDecoderInterceptor } from './core/services/http/html-decoder.interceptor';
import { TokenInterceptor } from './core/services/http/token.interceptor';
import { CostEstimatorModule } from './cost-estimator/cost-estimator.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { DeliveryDefenseModule } from './delivery-defense/delivery-defense.module';
import { DropShipmentModule } from './drop-shipment/drop-shipment.module';
import { GroupPolicyITComponent } from './group-policy-it/group-policy-it.component';
import { HistoryModule } from './history/history.module';
import { LoginModule } from './login/login.module';
import { LogoutComponent } from './logout/logout.component';
import { mockRegistryFactories } from './mocks/mock-registry-factories';
import { PreLoadService } from './preLoad.service';
import { S3FileDownloaderModule } from './shared/components/s3-file-downloader/s3-file-downloader.module';
import { SharedModule } from './shared/shared.module';
import { SignUpModule } from './sign-up/sign-up.module';
import { SupportModule } from './support/support.module';
import { TermsModule } from './terms/terms.module';
import { WorldShipUploadModule } from './world-ship-upload/world-ship-upload.module';

export function initEndpointStatus(
    apiHealthService: ApiHealthService,
    apiEndpointService: ApiEndpointService,
) {
    return () => {
        if (!mockRegistryFactories.length) {
            return of([]);
        }

        // 1) Instantiate each mock entry
        const registry = mockRegistryFactories.map(fn => fn(apiEndpointService));

        // 2) Define dummy params for :claimId, :messageId, etc.
        const dummyParams: Record<string, string> = {
            claimId: '1',
            messageId: '0001',
        };

        // 3) Turn each urlTemplate into a real URL
        const urlsToCheck = registry.map(r =>
            r.urlTemplate.replace(
                /:([A-Za-z0-9_]+)/g,
                (
                    _,
                    name,
                ) => dummyParams[name] || '',
            ),
        );

        // 4) Probe them in parallel, caching results inside ApiHealthService
        return firstValueFrom(
            apiHealthService.checkEndpoints(urlsToCheck).pipe(defaultIfEmpty([])),
        );
    };
}

// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function preLoadServiceFactory(provider: PreLoadService) {
    return () => provider.load();
}

export function recaptchaSiteKeyFactory(environmentService: EnvironmentService): string {
    return environmentService.environment.reCAPTCHAv3SiteKey;
}

@NgModule({
    bootstrap: [
        AppComponent,
    ],
    declarations: [
        AppComponent,
        LogoutComponent,
        GroupPolicyITComponent,
    ],
    imports: [
        CoreModule,
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        LoginModule,
        SignUpModule,
        SharedModule,
        DashboardModule,
        DeliveryDefenseModule,
        HistoryModule,
        AddressBookModule,
        AccountModule,
        BillingModule,
        SupportModule,
        CostEstimatorModule,
        DropShipmentModule,
        TermsModule,
        MatExpansionModule,
        MatTabsModule,
        WorldShipUploadModule,
        S3FileDownloaderModule,
        MatButtonToggleModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient],
            },
            missingTranslationHandler: { provide: MissingTranslationHandler, useClass: FallbackTranslationHandler },
        }),
        Angulartics2Module.forRoot({
            pageTracking: {
                clearIds: true,
                // idsRegExp: /^[a-z]\d+$/,
                clearQueryParams: true,
            },
        }),
        HammerModule,
        NgxSkeletonLoaderModule.forRoot({ animation: 'progress' }),
        RecaptchaModule,
        HistoryModule,
        BehaviorTrackerModule,
        SessionIdleModule.forRoot(
            environment.feature?.sessionIdle
                ? {
                    warningDurationMs: (SESSION_IDLE_CONFIG?.idleWarningTimeInSeconds || 0) * 1000,
                    timeoutDurationMs: (SESSION_IDLE_CONFIG?.idleTimeoutTimeInSeconds || 0) * 1000,
                }
                : {
                    warningDurationMs: 0,
                    timeoutDurationMs: 0,
                },
        ),
        DebugButtonComponent,
    ],
    providers: [
        ApiHealthService,
        ApiEndpointService,
        PreLoadService,
        EnvironmentService,
        ...(isDevMode()
                ? [
                    {
                        provide: APP_INITIALIZER,
                        useFactory: initEndpointStatus,
                        deps: [ApiHealthService, ApiEndpointService],
                        multi: true,
                    },
                    { provide: HTTP_INTERCEPTORS, useClass: MockHttpInterceptor, multi: true },
                ]
                : []
        ),
        { provide: APP_INITIALIZER, useFactory: preLoadServiceFactory, deps: [PreLoadService], multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HtmlDecoderInterceptor, multi: true },
        ...(
            environment?.feature?.jsonCacheBust
                ? [{ provide: HTTP_INTERCEPTORS, useClass: JsonCacheBustInterceptor, multi: true }]
                : []
        ),
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
        { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 2500 } },
        CookieService,
        ReCaptchaV3Service,
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useFactory: recaptchaSiteKeyFactory,
            deps: [EnvironmentService],
        },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {
}
